import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { DuplicateCandidatesError, mapApiError } from "../../lib/api";
import { getErrorMessage } from "../../lib/errorMessage";
import { enqueue } from "../../lib/offlineQueue/queue";

import { type StationCreate, type StationOut } from "./api";
import { DedupeCandidates } from "./DedupeCandidates";
import { useGeolocation, type GeolocationState } from "../map/useGeolocation";
import { uploadStationPhoto } from "./photoUpload";

type Status =
  | { kind: "idle" }
  | { kind: "uploading" }
  | { kind: "submitting" }
  | { kind: "success" }
  | { kind: "duplicates"; candidates: StationOut[] }
  | { kind: "rate-limited"; retryAfter: number }
  | { kind: "spam-detected" }
  | { kind: "validation" }
  | { kind: "unauthorized" }
  | { kind: "network" }
  | { kind: "server" }
  | { kind: "error"; message: string };

type Props = {
  onSuccess?: () => void;
};

export function AddStationForm({ onSuccess }: Props) {
  const { t } = useTranslation();
  const location = useGeolocation();
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const photoInputRef = useRef<HTMLInputElement>(null);

  const disabledReason = getDisabledReason(name, location.status, status, t);
  const submitDisabled = disabledReason !== null;

  async function submit() {
    let photoUrl: string | null = null;
    if (photo) {
      setStatus({ kind: "uploading" });
      try {
        photoUrl = await uploadStationPhoto(photo);
      } catch (e) {
        setStatus({
          kind: "error",
          message: getErrorMessage(e, t("stations.photo_upload_error")),
        });
        return;
      }
    }

    setStatus({ kind: "submitting" });
    const payload: StationCreate = {
      name: name.trim(),
      lat: location.lat,
      lng: location.lng,
      address: address.trim() || null,
      photo_url: photoUrl,
    };

    try {
      await enqueue({ endpoint: "POST /stations", body: payload });
      setStatus({ kind: "success" });
      onSuccess?.();
    } catch (err) {
      setStatus(mapError(err));
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="space-y-4"
    >
      <fieldset>
        <label className="block text-sm font-semibold text-fg-mute" htmlFor="station-name">
          {t("stations.name_label")}
        </label>
        <input
          id="station-name"
          required
          minLength={1}
          maxLength={200}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 w-full rounded-md border border-line px-3 py-2 text-sm"
        />
      </fieldset>

      <fieldset>
        <label className="block text-sm font-semibold text-fg-mute" htmlFor="station-address">
          {t("stations.address_label")}
        </label>
        <input
          id="station-address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          className="mt-1 w-full rounded-md border border-line px-3 py-2 text-sm"
        />
      </fieldset>

      <fieldset>
        <label className="block text-sm font-semibold text-fg-mute" htmlFor="station-photo">
          {t("stations.photo_label")}
        </label>
        {/* Native file input is visually hidden but still focusable/clickable
           via the label below, so keyboard + screen-reader behaviour matches
           a plain <input type="file">. Visible UI is themed buttons. */}
        <input
          id="station-photo"
          ref={photoInputRef}
          type="file"
          accept="image/*"
          onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
          className="sr-only"
        />
        {photo ? (
          <div className="mt-2 flex items-center gap-3 rounded-md border border-line bg-surface px-3 py-2 text-sm">
            <span aria-hidden="true" className="text-volt">📎</span>
            <span className="min-w-0 flex-1 truncate text-fg">{photo.name}</span>
            <span className="num shrink-0 text-xs text-fg-mute">
              {formatFileSize(photo.size)}
            </span>
            <button
              type="button"
              onClick={() => {
                setPhoto(null);
                if (photoInputRef.current) photoInputRef.current.value = "";
              }}
              className="shrink-0 text-xs font-medium text-fg-mute underline-offset-2 hover:text-ember hover:underline"
            >
              {t("stations.photo_remove")}
            </button>
          </div>
        ) : (
          <label
            htmlFor="station-photo"
            className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-line-strong bg-surface/40 px-3 py-3 text-sm font-medium text-fg-mute transition-colors hover:border-volt/60 hover:bg-surface hover:text-fg focus-within:border-volt"
          >
            <span aria-hidden="true">＋</span>
            {t("stations.photo_choose")}
          </label>
        )}
        <p className="mt-1 text-xs text-fg-mute">{t("stations.photo_review_note")}</p>
      </fieldset>

      <p className="rounded-md bg-surface-raised px-3 py-2 text-xs text-fg-mute">
        {t("stations.chargers_later_note")}
      </p>

      <LocationStatus location={location} />

      <StatusBanner status={status} onCancelDuplicates={() => setStatus({ kind: "idle" })} />

      <div className="space-y-1.5">
        <button
          type="submit"
          disabled={submitDisabled}
          className="btn-volt inline-flex items-center justify-center px-5 py-2.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
        >
          {status.kind === "uploading" && t("stations.submit_uploading")}
          {status.kind === "submitting" && t("stations.submit_sending")}
          {status.kind !== "uploading" && status.kind !== "submitting" && t("stations.submit_create")}
        </button>
        {disabledReason && (
          <p className="text-xs text-fg-faint" role="note">
            {disabledReason}
          </p>
        )}
      </div>
    </form>
  );
}

function getDisabledReason(
  name: string,
  locationStatus: GeolocationState["status"],
  status: Status,
  t: (k: string) => string,
): string | null {
  if (status.kind === "uploading") return t("stations.submit_disabled_uploading");
  if (status.kind === "submitting") return t("stations.submit_disabled_submitting");
  if (name.trim().length === 0) return t("stations.submit_disabled_name");
  if (locationStatus === "loading") return t("stations.submit_disabled_location");
  return null;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function LocationStatus({ location }: { location: GeolocationState }) {
  const { t } = useTranslation();
  const detail = `${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}`;
  const tone = location.status === "granted"
    ? { dot: "bg-volt", label: t("stations.location_status_using") }
    : location.status === "loading"
      ? { dot: "bg-fg-faint animate-pulse", label: t("stations.location_status_loading") }
      : { dot: "bg-ember", label: t("stations.location_status_fallback") };
  return (
    <div className="flex items-center gap-2 text-xs text-fg-mute">
      <span aria-hidden="true" className={`h-1.5 w-1.5 shrink-0 rounded-full ${tone.dot}`} />
      <span>{tone.label}</span>
      <span aria-hidden="true" className="text-fg-faint">·</span>
      <span className="num text-fg-faint" title={t("stations.location_coords_title")}>
        {detail}
      </span>
    </div>
  );
}

function StatusBanner({
  status,
  onCancelDuplicates,
}: {
  status: Status;
  onCancelDuplicates: () => void;
}) {
  const { t } = useTranslation();
  if (status.kind === "success") {
    return (
      <p role="status" className="text-sm text-volt-deep">
        {t("stations.create_success")}
      </p>
    );
  }
  if (status.kind === "duplicates") {
    return <DedupeCandidates candidates={status.candidates} onCancel={onCancelDuplicates} />;
  }
  if (status.kind === "rate-limited") {
    return (
      <p role="alert" className="text-sm text-ember">
        {t("errors.rate_limited_generic", { seconds: status.retryAfter })}
      </p>
    );
  }
  if (status.kind === "spam-detected") {
    return <p role="alert" className="text-sm text-ember">{t("errors.spam_detected")}</p>;
  }
  if (status.kind === "validation") {
    return <p role="alert" className="text-sm text-ember">{t("errors.validation_short")}</p>;
  }
  if (status.kind === "unauthorized") {
    return <p role="alert" className="text-sm text-ember">{t("errors.unauthorized")}</p>;
  }
  if (status.kind === "network") {
    return <p role="alert" className="text-sm text-ember">{t("errors.network")}</p>;
  }
  if (status.kind === "server") {
    return <p role="alert" className="text-sm text-ember">{t("errors.server")}</p>;
  }
  if (status.kind === "error") {
    return (
      <p role="alert" className="text-sm text-ember">
        {t("stations.create_failed_with_reason", { reason: status.message || t("errors.unknown") })}
      </p>
    );
  }
  return null;
}

function mapError(err: unknown): Status {
  if (err instanceof DuplicateCandidatesError) {
    // Strict body validation — a malformed 409 (server contract drift) falls
    // through to the server-error banner instead of rendering a confusing
    // empty "Posibles duplicados" panel.
    const candidates = extractCandidates(err.body);
    if (candidates !== null) return { kind: "duplicates", candidates };
    console.warn("[AddStationForm] 409 body missing candidates", err.body);
    return { kind: "server" };
  }
  return mapApiError(err) as Status;
}

function extractCandidates(body: unknown): StationOut[] | null {
  if (body === null || typeof body !== "object") return null;
  const cs = (body as { candidates?: unknown }).candidates;
  if (!Array.isArray(cs)) return null;
  return cs as StationOut[];
}
